import { useCallback, useMemo, useReducer, useState } from 'react';
import { useParams } from 'react-router';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import { useDispatch } from 'store';
import {
  deleteAllForecastItem,
  deleteForecastItem,
  downloadForeCastCSV,
  getForecastsList,
  postForecast,
  putForecast,
  uploadForeCastCSV,
} from 'store/slices/forecasts';
import LightButton from 'ui-component/button/LightButton';
import PrimaryButton from 'ui-component/button/PrimaryButton';
import AnimateButton from 'ui-component/extended/AnimateButton';
import Loader from 'ui-component/Loader';
import DropzoneContainer from '../backTests/DropzoneContainer';
import ForecastChart from './ForecastChart';

// Constants
const DATE_FORMAT = 'YYYY-MM-DD';

const SIGNAL_OPTIONS = [
  { label: 'LONG', value: 'LONG' },
  { label: 'SHORT', value: 'SHORT' },
  { label: 'NEUTRAL', value: 'NEUTRAL' },
  { label: 'NA', value: 'NA' },
  { label: 'N/A', value: 'N/A' },
];

const HORIZON_OPTIONS = [
  { label: 'Short Term', value: 'Short Term' },
  { label: 'Long Term', value: 'Long Term' },
];

const INITIAL_FORECAST_STATE = {
  signal: '',
  interest: '',
  date_of_forecast: dayjs(),
  date_of_exit: '',
  horizon: '',
  long_count: '',
  short_count: '',
  legs: [],
};

const INITIAL_LEG_STATE = {
  asset: '',
  side: 'LONG',
  weight: '',
};

const DIALOG_TYPES = {
  NONE: 'NONE',
  ADD_EDIT: 'ADD_EDIT',
  DELETE: 'DELETE',
  DELETE_ALL: 'DELETE_ALL',
  UPLOAD: 'UPLOAD',
  LEG_ADD_EDIT: 'LEG_ADD_EDIT',
  LEG_DELETE: 'LEG_DELETE',
};

// Dialog state reducer
const dialogReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_DIALOG':
      return { type: action.dialogType, data: action.data };
    case 'CLOSE_DIALOG':
      return { type: DIALOG_TYPES.NONE, data: null };
    default:
      return state;
  }
};

// Validation
const getLegSideCounts = (legs = []) => {
  const normalizedLegs = Array.isArray(legs) ? legs : [];
  return {
    long: normalizedLegs.filter((leg) => leg.side === 'LONG').length,
    short: normalizedLegs.filter((leg) => leg.side === 'SHORT').length,
  };
};

const getCarryTradeSignal = (forecast) =>
  `LONG ${forecast.long_count || 0} / SHORT ${forecast.short_count || 0}`;

const validateForecast = (forecast, isCarryTrade = false) => {
  const errors = {};

  if (!isCarryTrade && !forecast.signal) {
    errors.signal = 'Signal is required';
  }

  if (forecast.interest === '' || forecast.interest === undefined) {
    errors.interest = 'Interest is required';
  }

  if (forecast.date_of_forecast === 'Invalid Date') {
    errors.date_of_forecast = 'Forecast Date is invalid';
  }

  if (forecast.date_of_exit === 'Invalid Date') {
    errors.date_of_exit = 'Exit Date is invalid';
  }

  if (!isCarryTrade && !forecast.horizon) {
    errors.horizon = 'Horizon is required';
  }

  if (isCarryTrade) {
    const longCount = Number(forecast.long_count);
    const shortCount = Number(forecast.short_count);
    const legCounts = getLegSideCounts(forecast.legs);

    if (
      forecast.long_count === '' ||
      forecast.long_count === undefined ||
      Number.isNaN(longCount) ||
      longCount < 0
    ) {
      errors.long_count = 'Long count is required';
    } else if (legCounts.long !== longCount) {
      errors.long_count = 'Long count must match LONG legs';
    }

    if (
      forecast.short_count === '' ||
      forecast.short_count === undefined ||
      Number.isNaN(shortCount) ||
      shortCount < 0
    ) {
      errors.short_count = 'Short count is required';
    } else if (legCounts.short !== shortCount) {
      errors.short_count = 'Short count must match SHORT legs';
    }
  }

  return errors;
};

const validateLeg = (leg, isCarryTrade = false) => {
  const errors = {};
  const weight = Number(leg.weight);

  if (!leg.asset || !leg.asset.trim()) {
    errors.asset = 'Asset is required';
  }

  if (!['LONG', 'SHORT'].includes(leg.side)) {
    errors.side = 'Side is required';
  }

  if (!isCarryTrade && (leg.weight === '' || leg.weight === undefined)) {
    errors.weight = 'Weight is required';
  } else if (leg.weight !== '' && leg.weight !== undefined && Number.isNaN(weight)) {
    errors.weight = 'Weight must be numeric';
  }

  return errors;
};

const normalizeLegs = (legs = []) =>
  legs
    .filter((leg) => leg?.asset || leg?.side || leg?.weight !== '')
    .map((leg) => ({
      asset: String(leg.asset || '').trim().toUpperCase(),
      side: leg.side,
      ...(leg.weight === '' || leg.weight === undefined || leg.weight === null
        ? {}
        : { weight: Number(leg.weight) }),
    }));

const getForecastId = (forecast) => forecast?._id || forecast?.id;

const buildForecastPayload = (forecast, assetId, isCarryTrade = false) => {
  const basePayload = {
    asset_id: assetId,
    signal: isCarryTrade ? getCarryTradeSignal(forecast) : forecast.signal,
    interest: forecast.interest,
    date_of_forecast: forecast.date_of_forecast || null,
    date_of_exit: forecast.date_of_exit || null,
    ...(getForecastId(forecast) ? { _id: getForecastId(forecast) } : {}),
  };

  if (!isCarryTrade) {
    return {
      ...basePayload,
      horizon: forecast.horizon,
    };
  }

  return {
    ...basePayload,
    is_carry_trade: true,
    long_count: Number(forecast.long_count),
    short_count: Number(forecast.short_count),
    legs: normalizeLegs(forecast.legs),
  };
};

// Utility function for CSV download
const downloadCSVFile = (csvData, filename) => {
  const blob = new Blob([csvData], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const ForecastTable = ({ selectedAsset }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { categoryId, subCategoryId } = useParams();
  const isCarryTrade = Boolean(selectedAsset?.is_carry_trade);

  // State management
  const [dialogState, dialogDispatch] = useReducer(dialogReducer, {
    type: DIALOG_TYPES.NONE,
    data: null,
  });
  const [forecastForm, setForecastForm] = useState(INITIAL_FORECAST_STATE);
  const [legForm, setLegForm] = useState(INITIAL_LEG_STATE);
  const [errors, setErrors] = useState({});
  const [legErrors, setLegErrors] = useState({});
  const [legDialogState, setLegDialogState] = useState({
    open: false,
    forecast: null,
    legIndex: null,
    mode: 'forecast-form',
  });
  const [legDeleteState, setLegDeleteState] = useState({
    open: false,
    forecast: null,
    legIndex: null,
  });
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Computed values
  const assetId = subCategoryId || categoryId;
  const assetType = subCategoryId ? 'assets' : 'portfolio';

  // Query key with dependencies
  const queryKey = useMemo(
    () => ['forecasts', assetId, assetType],
    [assetId, assetType]
  );

  // Fetch forecasts
  const {
    data: forecasts = [],
    refetch,
    isLoading,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await dispatch(
        getForecastsList({
          assetsId: assetId,
          limit: 100,
          skip: 0,
          type: assetType,
        })
      );
      return response.data;
    },
    refetchOnWindowFocus: false,
    onError: () => {
      toast.error('Failed to fetch forecasts');
    },
  });

  // Reverse forecasts to show latest first
  const forecastsData = useMemo(() => {
    return forecasts.slice().reverse();
  }, [forecasts]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: async (forecast) => {
      const payload = buildForecastPayload(forecast, assetId, isCarryTrade);
      const response = await dispatch(postForecast(payload));
      if (response.status !== 200) {
        throw new Error(response?.detail || 'Failed to add forecast');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Forecast added successfully');
      refetch();
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to add forecast');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (forecast) => {
      const payload = buildForecastPayload(
        forecast,
        assetId,
        isCarryTrade || Boolean(forecast?.is_carry_trade)
      );
      const response = await dispatch(putForecast(payload));
      if (response.status !== 200) {
        throw new Error(response?.detail || 'Failed to update forecast');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Forecast updated successfully');
      refetch();
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update forecast');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (forecastId) => {
      await dispatch(deleteForecastItem({ forecastId }));
    },
    onSuccess: () => {
      toast.success('Forecast deleted successfully');
      refetch();
      handleCloseDialog();
    },
    onError: () => {
      toast.error('Failed to delete forecast');
    },
  });

  const deleteAllMutation = useMutation({
    mutationFn: async () => {
      const id = assetId;
      return dispatch(deleteAllForecastItem({ id: id }));
    },
    onSuccess: () => {
      toast.success('All forecasts deleted successfully');
      refetch();
      handleCloseDialog();
    },
    onError: () => {
      toast.error('Failed to delete all forecasts');
    },
  });

  // Dialog handlers
  const handleCloseDialog = useCallback(() => {
    dialogDispatch({ type: 'CLOSE_DIALOG' });
    setForecastForm(INITIAL_FORECAST_STATE);
    setLegForm(INITIAL_LEG_STATE);
    setErrors({});
    setLegErrors({});
    setLegDialogState({
      open: false,
      forecast: null,
      legIndex: null,
      mode: 'forecast-form',
    });
    setLegDeleteState({ open: false, forecast: null, legIndex: null });
    setFile(null);
  }, []);

  const handleOpenAddDialog = useCallback(() => {
    setForecastForm({
      ...INITIAL_FORECAST_STATE,
      is_carry_trade: isCarryTrade,
    });
    dialogDispatch({ type: 'OPEN_DIALOG', dialogType: DIALOG_TYPES.ADD_EDIT });
  }, [isCarryTrade]);

  const handleOpenEditDialog = useCallback((row) => {
    const rowLegs = Array.isArray(row.original?.legs) ? row.original.legs : [];
    const legCounts = getLegSideCounts(rowLegs);
    setForecastForm({
      ...row.original,
      is_carry_trade: isCarryTrade || Boolean(row.original?.is_carry_trade),
      long_count: row.original?.long_count ?? legCounts.long,
      short_count: row.original?.short_count ?? legCounts.short,
      legs: rowLegs,
    });
    dialogDispatch({
      type: 'OPEN_DIALOG',
      dialogType: DIALOG_TYPES.ADD_EDIT,
      data: row,
    });
  }, [isCarryTrade]);

  const handleOpenDeleteDialog = useCallback((row) => {
    dialogDispatch({
      type: 'OPEN_DIALOG',
      dialogType: DIALOG_TYPES.DELETE,
      data: row,
    });
  }, []);

  const handleOpenDeleteAllDialog = useCallback(() => {
    dialogDispatch({
      type: 'OPEN_DIALOG',
      dialogType: DIALOG_TYPES.DELETE_ALL,
    });
  }, []);

  const handleOpenUploadDialog = useCallback(() => {
    dialogDispatch({ type: 'OPEN_DIALOG', dialogType: DIALOG_TYPES.UPLOAD });
  }, []);

  const handleLegInputChange = useCallback((field, value) => {
    setLegForm((prev) => ({ ...prev, [field]: value }));
    setLegErrors((prev) => ({ ...prev, [field]: '' }));
  }, []);

  const handleAddFormLeg = useCallback((side = 'LONG') => {
    setLegForm({ ...INITIAL_LEG_STATE, side });
    setLegErrors({});
    setLegDialogState({
      open: true,
      forecast: null,
      legIndex: null,
      mode: 'forecast-form',
    });
  }, []);

  const handleEditFormLeg = useCallback((leg, legIndex, side) => {
    setLegForm({
      asset: leg.asset || '',
      side: side || leg.side || 'LONG',
      weight: leg.weight ?? '',
    });
    setLegErrors({});
    setLegDialogState({
      open: true,
      forecast: null,
      legIndex,
      mode: 'forecast-form',
    });
  }, []);

  const handleDeleteFormLeg = useCallback((legIndex) => {
    setForecastForm((prev) => ({
      ...prev,
      legs: (prev.legs || []).filter((_, index) => index !== legIndex),
    }));
  }, []);

  const handleCloseLegDialog = useCallback(() => {
    setLegDialogState({
      open: false,
      forecast: null,
      legIndex: null,
      mode: 'forecast-form',
    });
    setLegForm(INITIAL_LEG_STATE);
    setLegErrors({});
  }, []);

  const handleAddForecastLeg = useCallback((forecast, side = 'LONG') => {
    setLegForm({ ...INITIAL_LEG_STATE, side });
    setLegErrors({});
    setLegDialogState({
      open: true,
      forecast,
      legIndex: null,
      mode: 'forecast-row',
    });
  }, []);

  const handleEditForecastLeg = useCallback((forecast, leg, legIndex, side) => {
    setLegForm({
      asset: leg.asset || '',
      side: side || leg.side || 'LONG',
      weight: leg.weight ?? '',
    });
    setLegErrors({});
    setLegDialogState({
      open: true,
      forecast,
      legIndex,
      mode: 'forecast-row',
    });
  }, []);

  const handleOpenDeleteLegDialog = useCallback((forecast, legIndex) => {
    setLegDeleteState({ open: true, forecast, legIndex });
  }, []);

  const handleCloseDeleteLegDialog = useCallback(() => {
    setLegDeleteState({ open: false, forecast: null, legIndex: null });
  }, []);

  const updateForecastLegs = useCallback(
    async (forecast, nextLegs) => {
      const nextForecast = {
        ...forecast,
        legs: normalizeLegs(nextLegs),
        _id: getForecastId(forecast),
      };
      if (isCarryTrade || forecast?.is_carry_trade) {
        const legCounts = getLegSideCounts(nextLegs);
        nextForecast.is_carry_trade = true;
        nextForecast.long_count = legCounts.long;
        nextForecast.short_count = legCounts.short;
      }
      await updateMutation.mutateAsync({
        ...nextForecast,
      });
    },
    [isCarryTrade, updateMutation]
  );

  const handleSaveLeg = useCallback(async () => {
    const validationErrors = validateLeg(legForm, isCarryTrade);
    if (Object.keys(validationErrors).length > 0) {
      setLegErrors(validationErrors);
      return;
    }

    const normalizedLeg = normalizeLegs([legForm])[0];

    if (legDialogState.mode === 'forecast-form') {
      setForecastForm((prev) => {
        const currentLegs = Array.isArray(prev.legs) ? prev.legs : [];
        const nextLegs =
          legDialogState.legIndex === null
            ? [...currentLegs, normalizedLeg]
            : currentLegs.map((leg, index) =>
                index === legDialogState.legIndex ? normalizedLeg : leg
              );

        return { ...prev, legs: nextLegs };
      });
      handleCloseLegDialog();
      return;
    }

    const currentForecast = legDialogState.forecast;
    const currentLegs = Array.isArray(currentForecast?.legs)
      ? currentForecast.legs
      : [];
    const nextLegs =
      legDialogState.legIndex === null
        ? [...currentLegs, normalizedLeg]
        : currentLegs.map((leg, index) =>
            index === legDialogState.legIndex ? normalizedLeg : leg
          );

    try {
      await updateForecastLegs(currentForecast, nextLegs);
      handleCloseLegDialog();
    } catch (error) {
      // React Query onError shows the toast; keep rejected mutations out of the dev overlay.
    }
  }, [
    legForm,
    legDialogState,
    handleCloseLegDialog,
    isCarryTrade,
    updateForecastLegs,
  ]);

  const handleDeleteForecastLeg = useCallback(async () => {
    const currentForecast = legDeleteState.forecast;
    const nextLegs = (currentForecast?.legs || []).filter(
      (_, index) => index !== legDeleteState.legIndex
    );

    try {
      await updateForecastLegs(currentForecast, nextLegs);
      handleCloseDeleteLegDialog();
    } catch (error) {
      // React Query onError shows the toast; keep rejected mutations out of the dev overlay.
    }
  }, [legDeleteState, updateForecastLegs, handleCloseDeleteLegDialog]);

  // Form handlers
  const handleInputChange = useCallback((field, value) => {
    setForecastForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  }, []);

  const handleSubmitForecast = useCallback(async () => {
    const validationErrors = validateForecast(forecastForm, isCarryTrade);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (dialogState.data) {
        // Edit mode
        await updateMutation.mutateAsync(forecastForm);
      } else {
        // Add mode
        await createMutation.mutateAsync(forecastForm);
      }
    } catch (error) {
      // React Query onError shows the toast; keep rejected mutations out of the dev overlay.
    }
  }, [forecastForm, dialogState.data, isCarryTrade, createMutation, updateMutation]);

  const handleDeleteForecast = useCallback(async () => {
    if (dialogState.data?.original?._id) {
      await deleteMutation.mutateAsync(dialogState.data.original._id);
    }
  }, [dialogState.data, deleteMutation]);

  const handleDeleteAllForecasts = useCallback(async () => {
    await deleteAllMutation.mutateAsync();
  }, [deleteAllMutation]);

  // File handlers
  const handleUploadCSV = useCallback(async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await dispatch(uploadForeCastCSV(assetId, formData));

      if (response?.data?.message === 'Invalid CSV Format') {
        toast.error('Invalid CSV Format');
      } else {
        toast.success('Forecasts imported successfully');
        refetch();
        handleCloseDialog();
      }
    } catch (error) {
      toast.error('Error uploading file');
    }
  }, [file, assetId, dispatch, refetch, handleCloseDialog]);

  const handleDownloadCSV = useCallback(async () => {
    try {
      const type = subCategoryId ? 'assets' : 'portfolios';
      const response = await dispatch(downloadForeCastCSV(assetId, type));

      downloadCSVFile(response.data, `forecast_${assetId}.csv`);
      toast.success('Forecasts downloaded successfully');
    } catch (error) {
      toast.error('Error downloading file');
    }
  }, [assetId, subCategoryId, dispatch]);

  const renderLegsTable = useCallback(
    (legs, actions) => {
      const normalizedLegs = Array.isArray(legs) ? legs : [];

      if (normalizedLegs.length === 0) {
        return (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
              p: 3,
              textAlign: 'center',
            }}
          >
            <Typography color="text.secondary" variant="body2">
              No legs added
            </Typography>
          </Box>
        );
      }

      return (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderColor: 'divider',
            boxShadow: 'none',
            overflow: 'hidden',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 1.25, px: 2 }}>Asset</TableCell>
                <TableCell sx={{ py: 1.25, px: 2 }}>Side</TableCell>
                <TableCell sx={{ py: 1.25, px: 2 }} align="right">
                  Weight
                </TableCell>
                {actions && (
                  <TableCell sx={{ py: 1.25, px: 2 }} align="right">
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {normalizedLegs.map((leg, index) => (
                <TableRow
                  key={`${leg.asset || 'leg'}-${index}`}
                  sx={{
                    backgroundColor: 'background.paper',
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ py: 1.25, px: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {leg.asset || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 2 }}>
                    <Chip
                      size="small"
                      label={leg.side || 'N/A'}
                      color={leg.side === 'SHORT' ? 'error' : 'success'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ py: 1.25, px: 2 }} align="right">
                    {leg.weight === 0 || leg.weight ? leg.weight : 'N/A'}
                  </TableCell>
                  {actions && (
                    <TableCell sx={{ py: 1.25, px: 2 }} align="right">
                      <Tooltip title="Edit Leg">
                        <IconButton
                          size="small"
                          sx={{ color: 'text.secondary' }}
                          onClick={() => actions.onEdit(leg, index)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Leg">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => actions.onDelete(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    },
    []
  );

  const renderForecastLegPanel = useCallback(
    (forecast) => (
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="body2" fontWeight={700}>
            Legs
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleAddForecastLeg(forecast)}
          >
            Add Leg
          </Button>
        </Stack>
        {renderLegsTable(forecast.legs || [], {
          onEdit: (leg, legIndex) =>
            handleEditForecastLeg(forecast, leg, legIndex),
          onDelete: (legIndex) =>
            handleOpenDeleteLegDialog(forecast, legIndex),
        })}
      </Box>
    ),
    [
      handleAddForecastLeg,
      handleEditForecastLeg,
      handleOpenDeleteLegDialog,
      renderLegsTable,
    ]
  );

  const renderFormLegPanel = useCallback(
    () => (
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="body2" fontWeight={700}>
            Legs
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleAddFormLeg()}
          >
            Add Leg
          </Button>
        </Stack>
        {renderLegsTable(forecastForm.legs || [], {
          onEdit: (leg, legIndex) => handleEditFormLeg(leg, legIndex),
          onDelete: handleDeleteFormLeg,
        })}
      </Box>
    ),
    [
      forecastForm.legs,
      handleAddFormLeg,
      handleDeleteFormLeg,
      handleEditFormLeg,
      renderLegsTable,
    ]
  );

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'signal',
        header: 'Signal',
        Cell: ({ cell }) => {
          const signal = cell.getValue();
          const color =
            signal === 'SHORT'
              ? 'red'
              : signal === 'LONG'
              ? 'green'
              : 'inherit';
          return <span style={{ color, fontWeight: 500 }}>{signal}</span>;
        },
      },
      {
        accessorKey: 'interest',
        header: 'Interest (%)',
        Cell: ({ cell }) => `${cell.getValue() || 0}%`,
      },
      {
        accessorKey: 'horizon',
        header: 'Horizon',
      },
      {
        accessorKey: 'date_of_forecast',
        header: 'Forecast Date',
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? dayjs(value).format(DATE_FORMAT) : 'N/A';
        },
      },
      {
        accessorKey: 'date_of_exit',
        header: 'Exit Date',
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? dayjs(value).format(DATE_FORMAT) : 'N/A';
        },
      },
    ],
    []
  );

  // Table configuration
  const table = useMaterialReactTable({
    columns,
    data: forecastsData,
    getRowId: (row) => row._id || row.id,
    enableExpanding: isCarryTrade,
    enableEditing: true,
    positionActionsColumn: 'last',
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: 'background.paper',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      },
    },
    muiDetailPanelProps: {
      sx: {
        backgroundColor: '#f8fafc',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'divider',
        p: 0,
      },
    },
    muiPaginationProps: {
      showFirstButton: false,
      showLastButton: false,
    },
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => handleOpenEditDialog(row)} size="small">
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            color="error"
            onClick={() => handleOpenDeleteDialog(row)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderDetailPanel: isCarryTrade ? ({ row }) => (
      <Box
        sx={{
          backgroundColor: '#f8fafc',
          px: { xs: 2, md: 4 },
          py: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: { xs: 2, md: 3 },
          }}
        >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          mb={2}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Forecast Legs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Array.isArray(row.original.legs) && row.original.legs.length
                ? `${row.original.legs.length} leg${
                    row.original.legs.length === 1 ? '' : 's'
                  } configured`
                : 'No legs configured'}
            </Typography>
          </Box>
        </Stack>
        {renderForecastLegPanel(row.original)}
        </Box>
      </Box>
    ) : undefined,
    renderTopToolbarCustomActions: () => (
      <AnimateButton>
        <LightButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          title="Add Forecast"
          style={{ padding: '8px 20px' }}
        />
      </AnimateButton>
    ),
  });

  if (isLoading) {
    return <Loader />;
  }

  // if (isError) {
  //   return (
  //     <Box p={3}>
  //       <Typography color="error">Failed to load forecasts</Typography>
  //     </Box>
  //   );
  // }

  const isAddEditDialogOpen = dialogState.type === DIALOG_TYPES.ADD_EDIT;
  const isDeleteDialogOpen = dialogState.type === DIALOG_TYPES.DELETE;
  const isDeleteAllDialogOpen = dialogState.type === DIALOG_TYPES.DELETE_ALL;
  const isUploadDialogOpen = dialogState.type === DIALOG_TYPES.UPLOAD;
  const isEditMode = Boolean(dialogState.data);

  return (
    <Grid container spacing={3}>
      {/* Chart Section */}
      <Grid item xs={12} mt={4}>
        <ForecastChart />
      </Grid>

      {/* Action Buttons */}
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}>
          <AnimateButton>
            <PrimaryButton
              title="Import Forecast"
              onClick={handleOpenUploadDialog}
            />
          </AnimateButton>

          <AnimateButton>
            <PrimaryButton
              title="Export Forecast"
              onClick={handleDownloadCSV}
            />
          </AnimateButton>

          <Button
            variant="outlined"
            onClick={handleOpenDeleteAllDialog}
            startIcon={<DeleteIcon />}
            disabled={forecastsData.length === 0}
            sx={{
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                borderColor: 'error.dark',
                backgroundColor: 'error.light',
              },
              '&.Mui-disabled': {
                borderColor: 'action.disabled',
                color: 'action.disabled',
              },
            }}
          >
            Delete All
          </Button>
        </Stack>
      </Grid>

      {/* Table */}
      <Grid item xs={12}>
        <MaterialReactTable table={table} />
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog
        open={isAddEditDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="forecast-dialog-title"
      >
        <DialogTitle id="forecast-dialog-title">
          {isEditMode ? 'Edit Forecast' : 'Add Forecast'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {!isCarryTrade && (
              <Grid item xs={12}>
                <InputLabel required>Signal</InputLabel>
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={SIGNAL_OPTIONS}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.label
                  }
                  value={
                    SIGNAL_OPTIONS.find(
                      (opt) => opt.value === forecastForm.signal
                    ) || forecastForm.signal
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.value === (value?.value || value)
                  }
                  onChange={(_, newValue) =>
                    handleInputChange(
                      'signal',
                      typeof newValue === 'string'
                        ? newValue
                        : newValue?.value || ''
                    )
                  }
                  onInputChange={(_, newInputValue) =>
                    handleInputChange('signal', newInputValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Signal"
                      error={Boolean(errors.signal)}
                      helperText={errors.signal}
                    />
                  )}
                />
              </Grid>
            )}

            {isCarryTrade && (
              <>
                <Grid item xs={12} sm={6}>
                  <InputLabel required>Long Count</InputLabel>
                  <TextField
                    fullWidth
                    type="number"
                    placeholder="3"
                    value={forecastForm.long_count}
                    onChange={(e) =>
                      handleInputChange('long_count', e.target.value)
                    }
                    error={Boolean(errors.long_count)}
                    helperText={errors.long_count}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel required>Short Count</InputLabel>
                  <TextField
                    fullWidth
                    type="number"
                    placeholder="3"
                    value={forecastForm.short_count}
                    onChange={(e) =>
                      handleInputChange('short_count', e.target.value)
                    }
                    error={Boolean(errors.short_count)}
                    helperText={errors.short_count}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: '#f8fafc',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Signal
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {getCarryTradeSignal(forecastForm)}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <InputLabel required>
                {isCarryTrade ? 'Return (%)' : 'Interest (%)'}
              </InputLabel>
              <TextField
                fullWidth
                type="number"
                placeholder={
                  isCarryTrade
                    ? 'Enter return percentage'
                    : 'Enter interest percentage'
                }
                value={forecastForm.interest}
                onChange={(e) => handleInputChange('interest', e.target.value)}
                error={Boolean(errors.interest)}
                helperText={errors.interest}
                inputProps={
                  isCarryTrade
                    ? { step: 0.01 }
                    : { min: 0, max: 100, step: 0.01 }
                }
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel>
                {isCarryTrade ? 'Date of Signal' : 'Forecast Date'}
              </InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dayjs(forecastForm.date_of_forecast)}
                  onChange={(newValue) =>
                    handleInputChange(
                      'date_of_forecast',
                      newValue ? newValue.format(DATE_FORMAT) : ''
                    )
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(errors.date_of_forecast),
                      helperText: errors.date_of_forecast,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <InputLabel>
                {isCarryTrade ? 'End of Signal' : 'Exit Date'}
              </InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={
                    forecastForm.date_of_exit
                      ? dayjs(forecastForm.date_of_exit)
                      : null
                  }
                  onChange={(newValue) =>
                    handleInputChange(
                      'date_of_exit',
                      newValue ? newValue.format(DATE_FORMAT) : ''
                    )
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(errors.date_of_exit),
                      helperText: errors.date_of_exit,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {!isCarryTrade && (
              <Grid item xs={12}>
                <InputLabel required>Horizon</InputLabel>
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={HORIZON_OPTIONS}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option.label
                  }
                  value={
                    HORIZON_OPTIONS.find(
                      (opt) => opt.value === forecastForm.horizon
                    ) || forecastForm.horizon
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.value === (value?.value || value)
                  }
                  onChange={(_, newValue) =>
                    handleInputChange(
                      'horizon',
                      typeof newValue === 'string'
                        ? newValue
                        : newValue?.value || ''
                    )
                  }
                  onInputChange={(_, newInputValue) =>
                    handleInputChange('horizon', newInputValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Horizon"
                      error={Boolean(errors.horizon)}
                      helperText={errors.horizon}
                    />
                  )}
                />
              </Grid>
            )}

            {isCarryTrade && (
              <Grid item xs={12}>
                {renderFormLegPanel()}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <AnimateButton>
            <Button
              variant="contained"
              color="inherit"
              onClick={handleCloseDialog}
              sx={{ boxShadow: 'none' }}
            >
              Cancel
            </Button>
          </AnimateButton>
          <AnimateButton>
            <PrimaryButton
              onClick={handleSubmitForecast}
              title={isEditMode ? 'Update' : 'Add'}
              disabled={createMutation.isPending || updateMutation.isPending}
            />
          </AnimateButton>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Leg Dialog */}
      <Dialog
        open={legDialogState.open}
        onClose={handleCloseLegDialog}
        maxWidth="xs"
        fullWidth
        aria-labelledby="leg-dialog-title"
      >
        <DialogTitle id="leg-dialog-title">
          {legDialogState.legIndex === null ? 'Add Leg' : 'Edit Leg'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <InputLabel required>Asset</InputLabel>
              <TextField
                fullWidth
                placeholder="Enter asset, e.g. MXN"
                value={legForm.asset}
                onChange={(e) => handleLegInputChange('asset', e.target.value)}
                error={Boolean(legErrors.asset)}
                helperText={legErrors.asset}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel required>Side</InputLabel>
              <TextField
                select
                fullWidth
                value={legForm.side}
                onChange={(e) => handleLegInputChange('side', e.target.value)}
                error={Boolean(legErrors.side)}
                helperText={legErrors.side}
              >
                <MenuItem value="LONG">LONG</MenuItem>
                <MenuItem value="SHORT">SHORT</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <InputLabel required={!isCarryTrade}>
                Weight{isCarryTrade ? ' (optional)' : ''}
              </InputLabel>
              <TextField
                fullWidth
                type="number"
                placeholder="Enter weight, e.g. 0.333"
                value={legForm.weight}
                onChange={(e) => handleLegInputChange('weight', e.target.value)}
                error={Boolean(legErrors.weight)}
                helperText={legErrors.weight}
                inputProps={{ step: 0.001 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="inherit"
            onClick={handleCloseLegDialog}
            sx={{ boxShadow: 'none' }}
          >
            Cancel
          </Button>
          <PrimaryButton
            title={legDialogState.legIndex === null ? 'Add' : 'Update'}
            onClick={handleSaveLeg}
            disabled={updateMutation.isPending}
          />
        </DialogActions>
      </Dialog>

      {/* Delete Leg Dialog */}
      <Dialog
        open={legDeleteState.open}
        onClose={handleCloseDeleteLegDialog}
        aria-labelledby="delete-leg-dialog-title"
      >
        <DialogTitle id="delete-leg-dialog-title">Delete Leg</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this leg? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="info"
            onClick={handleCloseDeleteLegDialog}
            sx={{ color: 'white' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteForecastLeg}
            disabled={updateMutation.isPending}
            sx={{ color: 'white' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Forecast</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this forecast? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="info"
            onClick={handleCloseDialog}
            sx={{ color: 'white' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteForecast}
            disabled={deleteMutation.isPending}
            sx={{ color: 'white' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Dialog */}
      <Dialog
        open={isDeleteAllDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="delete-all-dialog-title"
      >
        <DialogTitle id="delete-all-dialog-title">
          Delete All Forecasts
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all forecasts (
            {forecastsData.length} items)? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="info"
            onClick={handleCloseDialog}
            sx={{ color: 'white' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAllForecasts}
            disabled={deleteAllMutation.isPending}
            sx={{ color: 'white' }}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={isUploadDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="upload-dialog-title"
      >
        <DialogTitle id="upload-dialog-title">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Upload Forecast</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{ color: theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <DropzoneContainer
              file={file}
              setFile={setFile}
              uploadProgress={uploadProgress}
              setUploadProgress={setUploadProgress}
              title="Drag and drop a CSV file here, or click the button below to select one"
              buttonName="Upload Forecast CSV"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <AnimateButton>
            <PrimaryButton
              title="Import"
              onClick={handleUploadCSV}
              disabled={!file}
              style={{ padding: '8px 20px' }}
            />
          </AnimateButton>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

// Query client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const Forecast = ({ selectedAsset }) => (
  <QueryClientProvider client={queryClient}>
    <ForecastTable selectedAsset={selectedAsset} />
  </QueryClientProvider>
);

export default Forecast;
